import {inject, Injectable} from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import {catchError, EMPTY, Observable, switchMap, throwError} from 'rxjs';
import {Exception} from '../models/exception';
import {ExceptionDispatcherService} from '../services/exception-dispatcher.service';
import {AuthService} from '../services/auth.service';
import {Router} from '@angular/router';
import {environment} from '../../environments/environment';
import {SKIP_EXCEPTION_HANDLING} from '../tokens/skip-exception-handling.token';

@Injectable()
export class ExceptionInterceptor implements HttpInterceptor {
  private readonly exceptionDispatcherService: ExceptionDispatcherService = inject(ExceptionDispatcherService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  private handleAuthError(): Observable<never> {
    this.authService.logout("exceptions.unauthorized.modal.subject", "exceptions.unauthorized.modal.body");
    return EMPTY;
  }

  private handleUnauthorizedError(error: HttpErrorResponse, httpRequest: HttpRequest<any>, httpHandler: HttpHandler): Observable<any> {
    // If the 401 error response is from the refresh token request, we log out the user,
    if (error.url?.includes('token/refresh')) {
      return this.handleAuthError();
    }
    // If the 401 error response is from the logout request, we redirect the user to the login page.
    if (error.url?.includes('logout')) {
      this.router.navigate(['/landing']).then();
      return EMPTY;
    }
    // If the error response is from a request other than the refresh token request, we refresh the token.
    // since the authorization interceptor won't be called again, we need to set the Authorization header manually.
    return this.authService.refreshToken().pipe(
      switchMap((response: HttpResponse<any>) => {
        if (response.ok) {
          const newRequest = httpRequest.clone();
          return httpHandler.handle(newRequest);
        } else {
          return this.handleAuthError();
        }
      }),
      catchError(() => this.handleAuthError())
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            if (req.url.includes(`${environment.collaboratorBasePath}/`)) {
              return next.handle(req);
            }
            return this.handleUnauthorizedError(error, req, next);
          } else {
            // Callers that render the error themselves opt out of the global exception dialog.
            if (!req.context.get(SKIP_EXCEPTION_HANDLING)) {
              this.exceptionDispatcherService.evaluate(error).process(error);
            }
            return throwError(() => new Exception(error));
          }
        }
      )
    );
  }
}
